import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType
} from '@ast-data/shared';
import { listSortComparator, type GridColDef } from '../../lib/dataGrid';
import { locationLabel } from './locationLabel';
import { OWN_LOCATION_FIELDS } from './findingRefSets';

import { compareNatural } from '../../utils/compare';

/**
 * Base editor path for a Befund, scoped to the app the user is in: office grids
 * route through `/office/projects/...`, the field tool through `/field/...`. The
 * SAME dialogs serve both apps, so anything they link to must be built from
 * here — a hardcoded `/office/...` drops a field user into the office tables.
 */
export function useFindingBasePath(): (
    projectId: string,
    findingId: string
) => string {
    const inField = useLocation().pathname.startsWith('/field');
    return useCallback(
        (projectId: string, findingId: string) =>
            inField
                ? `/field/${projectId}/findings/${findingId}`
                : `/office/projects/${projectId}/findings/${findingId}`,
        [inField]
    );
}

/**
 * Route to a Befund's own editor. Used by the description dialogs' "Befund {Nr}"
 * breadcrumb chip to jump up to the owning Befund.
 */
export function useFindingEditPath(): (
    projectId: string,
    findingId: string
) => string {
    const findingBase = useFindingBasePath();
    return useCallback(
        (projectId: string, findingId: string) =>
            `${findingBase(projectId, findingId)}/edit`,
        [findingBase]
    );
}

/**
 * The Befund-description reference label — "Bef. 7000, Fl. 1, Pl. 1" — shared by
 * the FindingDescriptionPicker chips AND the grid columns, so both read the same.
 * A whole-Befund ref is just "Bef. 7000". The grammar lives in `locationLabel`,
 * which the Befund PDF's footer uses too: one Lagezuordnung, written one way.
 */

/** "Bef. {Nr}, Fl. {X}, Pl. {Y}[, Teil {T}]" for a Planum description. */
export function planumRefLabel(
    number: string,
    planum: FindingPlanumDocType
): string {
    return locationLabel({
        findingNumber: number,
        area: planum.area,
        planumNumber: planum.planumNumber,
        part: planum.partLabel
    });
}

/** "Bef. {Nr}, Fl. {X}, Pr. {Y}[, Teil {T}]" for a Profil description. */
export function profileRefLabel(
    number: string,
    profile: FindingProfileDocType
): string {
    return locationLabel({
        findingNumber: number,
        area: profile.area,
        profileNumber: profile.profileNumber,
        part: profile.partLabel
    });
}

/** A DataGrid `multiSelect` option: the ref id + the label the picker shows. */
export interface FindingRefOption {
    value: string;
    label: string;
}

/** A resolved Befund reference: how to label it and where its editor lives. */
export interface FindingRefInfo {
    kind: 'planum' | 'profile' | 'finding';
    label: string;
    /** Route to that description's / Befund's editor, scoped to ITS own project. */
    to: string;
}

/**
 * Map every family ref id (planum / profile / finding) to its label + editor
 * route, so a chip can both read correctly and link through. Routes are scoped
 * to the referenced record's OWN project — a family ref may live in another
 * phase.
 */
export function useFindingRefResolver(
    findings: FindingDocType[],
    planums: FindingPlanumDocType[],
    profiles: FindingProfileDocType[]
): Map<string, FindingRefInfo> {
    const findingBase = useFindingBasePath();
    return useMemo(() => {
        const numberByFindingId = new Map(
            findings.map((finding) => [finding.id, finding.findingNumber])
        );
        const map = new Map<string, FindingRefInfo>();
        for (const planum of planums) {
            const number = numberByFindingId.get(planum.findingId);
            if (!number) {
                continue;
            }
            map.set(planum.id, {
                kind: 'planum',
                label: planumRefLabel(number, planum),
                to: `${findingBase(planum.projectId, planum.findingId)}/planums/${planum.id}/edit`
            });
        }
        for (const profile of profiles) {
            const number = numberByFindingId.get(profile.findingId);
            if (!number) {
                continue;
            }
            map.set(profile.id, {
                kind: 'profile',
                label: profileRefLabel(number, profile),
                to: `${findingBase(profile.projectId, profile.findingId)}/profiles/${profile.id}/edit`
            });
        }
        for (const finding of findings) {
            if (!finding.findingNumber) {
                continue;
            }
            map.set(finding.id, {
                kind: 'finding',
                label: locationLabel({ findingNumber: finding.findingNumber }),
                to: `${findingBase(finding.projectId, finding.id)}/edit`
            });
        }
        return map;
    }, [findings, planums, profiles, findingBase]);
}

/**
 * The `valueOptions` for a `multiSelect` Befund column: every Planum/Profil
 * description plus every Befund as a bare whole-Befund option. Pass alongside a
 * `valueGetter` that flattens the row's ref arrays into their ids — the built-in
 * multiSelect cell then renders each as a chip and drives filter/export.
 */
export function useRefFindingOptions(
    findings: FindingDocType[],
    planums: FindingPlanumDocType[],
    profiles: FindingProfileDocType[]
): FindingRefOption[] {
    return useMemo(() => {
        const numberByFindingId = new Map(
            findings.map((finding) => [finding.id, finding.findingNumber])
        );
        const options: FindingRefOption[] = [];
        for (const planum of planums) {
            const number = numberByFindingId.get(planum.findingId);
            if (number) {
                options.push({
                    value: planum.id,
                    label: planumRefLabel(number, planum)
                });
            }
        }
        for (const profile of profiles) {
            const number = numberByFindingId.get(profile.findingId);
            if (number) {
                options.push({
                    value: profile.id,
                    label: profileRefLabel(number, profile)
                });
            }
        }
        for (const finding of findings) {
            if (finding.findingNumber) {
                options.push({
                    value: finding.id,
                    label: locationLabel({
                        findingNumber: finding.findingNumber
                    })
                });
            }
        }
        return options;
    }, [findings, planums, profiles]);
}

/**
 * The ref arrays a Befund-referencing record carries. A Probe names only a
 * description, never a whole Befund, so it has no `findingIds`.
 */
export interface RowWithRefs {
    planumIds: string[];
    profileIds: string[];
    findingIds?: string[];
}

/** Distinct, non-empty, naturally-sorted values. */
function distinct(values: string[]): string[] {
    return [...new Set(values.filter(Boolean))].sort(
        (firstValue, secondValue) => compareNatural(firstValue, secondValue)
    );
}

/** The four hidden Befund filter columns' field names. */
export const FINDING_FILTER_FIELDS = [
    'findingRefNumber',
    'findingRefArea',
    'findingRefPlanum',
    'findingRefProfile'
] as const;

/** `columnVisibilityModel` entries that hide the four filter columns by default. */
export const HIDDEN_FINDING_FILTER_COLUMNS: Record<string, boolean> = {
    findingRefNumber: false,
    findingRefArea: false,
    findingRefPlanum: false,
    findingRefProfile: false
};

/**
 * Four HIDDEN, filterable `multiSelect` columns — Befundnummer / Fläche /
 * Planum / Profil — appended to a Befund-referencing grid so the filter panel
 * can narrow by each dimension of a record's refs (the visible chips column only
 * filters by whole description). A hidden column is still listed in the filter
 * column picker. Each cell value is the record's distinct set for that
 * dimension; typed text matches entry by entry, the `containsAnyOf` pick
 * "references any" (see listFilterOperators).
 */
export function useFindingRefFilterColumns<T extends RowWithRefs>(
    findings: FindingDocType[],
    planums: FindingPlanumDocType[],
    profiles: FindingProfileDocType[]
): GridColDef<T>[] {
    const intl = useIntl();
    return useMemo(() => {
        const numberByFindingId = new Map(
            findings.map((finding) => [finding.id, finding.findingNumber])
        );
        const planumById = new Map(
            planums.map((planum) => [planum.id, planum])
        );
        const profileById = new Map(
            profiles.map((profile) => [profile.id, profile])
        );

        const numbersOf = (row: RowWithRefs): string[] => {
            const numbers: string[] = [];
            for (const id of row.planumIds) {
                const planum = planumById.get(id);
                const number =
                    planum && numberByFindingId.get(planum.findingId);
                if (number) {
                    numbers.push(number);
                }
            }
            for (const id of row.profileIds) {
                const profile = profileById.get(id);
                const number =
                    profile && numberByFindingId.get(profile.findingId);
                if (number) {
                    numbers.push(number);
                }
            }
            for (const id of row.findingIds ?? []) {
                const number = numberByFindingId.get(id);
                if (number) {
                    numbers.push(number);
                }
            }
            return distinct(numbers);
        };
        const areasOf = (row: RowWithRefs): string[] =>
            distinct([
                ...row.planumIds.map((id) => planumById.get(id)?.area ?? ''),
                ...row.profileIds.map((id) => profileById.get(id)?.area ?? '')
            ]);
        const planumsOf = (row: RowWithRefs): string[] =>
            distinct(
                row.planumIds.map(
                    (id) => planumById.get(id)?.planumNumber ?? ''
                )
            );
        const profilesOf = (row: RowWithRefs): string[] =>
            distinct(
                row.profileIds.map(
                    (id) => profileById.get(id)?.profileNumber ?? ''
                )
            );

        // Named apart from a record's own Lage columns (Fläche, Planum,
        // Profil — see OWN_LOCATION_FIELDS): these come from the linked
        // descriptions.
        const linkedHeader = (id: string) =>
            intl.formatMessage(
                { id: 'findingRef.linkedColumn' },
                { label: intl.formatMessage({ id }) }
            );

        const base = {
            type: 'multiSelect' as const,
            filterable: true,
            // The values are lists; sorted entry by entry (see the comparator).
            sortComparator: listSortComparator,
            width: 130
        };

        return [
            {
                ...base,
                field: 'findingRefNumber',
                // Alone in its word: no record has a Befundnummer of its own.
                headerName: intl.formatMessage({ id: 'findingRef.number' }),
                valueOptions: distinct(
                    findings.map((finding) => finding.findingNumber)
                ),
                valueGetter: (_value, row: T) => numbersOf(row)
            },
            {
                ...base,
                field: 'findingRefArea',
                headerName: linkedHeader('findingRef.area'),
                valueOptions: distinct([
                    ...planums.map((planum) => planum.area),
                    ...profiles.map((profile) => profile.area)
                ]),
                valueGetter: (_value, row: T) => areasOf(row)
            },
            {
                ...base,
                field: 'findingRefPlanum',
                headerName: linkedHeader('findingRef.planum'),
                valueOptions: distinct(
                    planums.map((planum) => planum.planumNumber)
                ),
                valueGetter: (_value, row: T) => planumsOf(row)
            },
            {
                ...base,
                field: 'findingRefProfile',
                headerName: linkedHeader('findingRef.profile'),
                valueOptions: distinct(
                    profiles.map((profile) => profile.profileNumber)
                ),
                valueGetter: (_value, row: T) => profilesOf(row)
            }
        ];
    }, [findings, planums, profiles, intl]);
}

/**
 * The header of a record grid's detail column. The own Lage shares its words
 * with the Befund-reference filter columns above ("Fläche", "Planum",
 * "Profil"), so both sides say which they are: "Planum (Lage)" is typed into
 * the record, "Planum (Verknüpfung)" comes from a linked description.
 */
export function useDetailColumnHeader(
    labelPrefix: string
): (field: string) => string {
    const intl = useIntl();
    return (field) => {
        const label = intl.formatMessage({
            id: `${labelPrefix}.field.${field}`
        });
        return (OWN_LOCATION_FIELDS as readonly string[]).includes(field)
            ? intl.formatMessage(
                  { id: 'findingRef.ownLocationColumn' },
                  { label }
              )
            : label;
    };
}
